/**
 * The assistant's HTTP surface.
 *
 * Requests go up over HTTP; replies come back over Ably. Sending a message
 * therefore returns as soon as the backend has accepted it — there is no
 * response body to stream and nothing to await beyond the acknowledgement.
 * Everything the user will see arrives on the conversation's channel.
 */

import { buildAssistantHeaders, buildAssistantUrl } from "./assistantApi";
import { AI_ASSISTANT_CONSTANTS } from "../constants/aiAssistantConstants";

async function request(path, { method = "GET", body, signal } = {}) {
  const response = await fetch(buildAssistantUrl(path), {
    method,
    headers: await buildAssistantHeaders(),
    credentials: "include",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    signal,
  });

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const payload = await response.json();
      if (payload?.detail) detail = payload.detail;
    } catch {
      // A non-JSON error body tells us nothing the status has not already.
    }
    const error = new Error(detail || "Request failed");
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
}

/** A realtime token scoped to this user's channels.

 * The path comes from the shared constant rather than being written here.
 * It was spelled two ways - `ably/token` in this file, `ably-token` in the
 * constant and on the backend - so every token request 404'd and the realtime
 * connection never came up, while the rest of the assistant carried on
 * working over plain HTTP.
 */
export const fetchAblyToken = () =>
  request(AI_ASSISTANT_CONSTANTS.ABLY.AUTH_PATH, { method: "POST" });

/** The history list, most recently updated first. */
export const listConversations = ({ limit = 30, cursor } = {}) => {
  const query = new URLSearchParams({ limit: String(limit) });
  if (cursor) query.set("cursor", cursor);
  return request(`conversations?${query.toString()}`);
};

export const createConversation = (title) =>
  request("conversations", { method: "POST", body: { title: title ?? null } });

export const getConversation = (conversationId) =>
  request(`conversations/${encodeURIComponent(conversationId)}`);

export const renameConversation = (conversationId, title) =>
  request(`conversations/${encodeURIComponent(conversationId)}`, {
    method: "PATCH",
    body: { title },
  });

export const deleteConversation = (conversationId) =>
  request(`conversations/${encodeURIComponent(conversationId)}`, {
    method: "DELETE",
  });

/**
 * The transcript. This is what a page renders when a conversation opens —
 * the server is the only record of it, so nothing is read from the browser.
 */
export const listTurns = (conversationId, { limit = 50, beforeSeq } = {}) => {
  const query = new URLSearchParams({ limit: String(limit) });
  if (beforeSeq) query.set("before_seq", String(beforeSeq));
  return request(
    `conversations/${encodeURIComponent(conversationId)}/turns?${query.toString()}`,
  );
};

/**
 * One turn, whole.
 *
 * The repair path when the live stream has a hole in it: rather than trying
 * to stitch around missing pieces, refetch the turn and render it again.
 */
export const getTurn = (conversationId, turnId) =>
  request(
    `conversations/${encodeURIComponent(conversationId)}/turns/${encodeURIComponent(turnId)}`,
  );

/**
 * Sends a message. Resolves once the backend has accepted it, which is not
 * the same as answered: what comes back is the turn id to follow, and the
 * reply arrives on the conversation channel.
 *
 * A 409 means a turn is already running in this conversation.
 */
export const sendMessage = (conversationId, { text, screenContext, confirmDeepResearch = false }) =>
  request(`conversations/${encodeURIComponent(conversationId)}/messages`, {
    method: "POST",
    body: {
      text,
      screen_context: screenContext ?? null,
      confirm_deep_research: confirmDeepResearch,
    },
  });

/**
 * Asks the backend to stop a running turn.
 *
 * Returns `stopping` or `already_finished`. Both are successes: the second
 * means the reply landed as the user reached for the button, and there is
 * nothing to stop. Neither is worth an error message.
 *
 * What actually ends the turn on screen is the `conversation.turn_stopped`
 * event on the channel, not this response — so a stop initiated in one tab
 * settles in every tab.
 */
export const stopTurn = (conversationId, turnId) =>
  request(
    `conversations/${encodeURIComponent(conversationId)}/turns/${encodeURIComponent(turnId)}/stop`,
    { method: "POST" },
  );

/**
 * Rates a turn. `value` is "up", "down", or null to clear it.
 *
 * Stored against the turn rather than held in the component, so the rating
 * survives a reload and comes back with the transcript.
 */
export const sendTurnFeedback = (conversationId, turnId, value, comment) =>
  request(
    `conversations/${encodeURIComponent(conversationId)}/turns/${encodeURIComponent(turnId)}/feedback`,
    { method: "POST", body: { value, comment: comment ?? null } },
  );

/** What Lou could see when it answered this turn, and what it changed. */
export const getTurnContext = (conversationId, turnId) =>
  request(
    `conversations/${encodeURIComponent(conversationId)}/turns/${encodeURIComponent(turnId)}/context`,
  );

